// src/contexts/AuthContext.tsx
import React, {
	createContext,
	useState,
	useEffect,
	useCallback,
	useContext,
	useRef,
} from "react";
import { apiClient } from "../utils/api";
import { getAuthToken, clearAuthToken } from "../utils/tokenStorage";
import { getAccountLoginUrl, getTicketAppHomeUrl } from "../utils/accountApp";

/** 名前の型 */
interface Name {
	firstName: string;
	lastName: string;
}

/** 住所の型 */
interface Address {
	postal_code?: string;
	prefecture?: string;
	city?: string;
	street_address?: string;
	building_name?: string;
}

/** ユーザー情報の型 */
interface User {
	id: number;
	email: string;
	name?: Name;
	address?: Address | null;
}

/** AuthContext が持つ状態と操作 */
interface AuthState {
	user: User | null;
	isAuthenticated: boolean;
	isAuthLoading: boolean;
	hasFetched: boolean;
	checkAuth: () => Promise<void>;
	login: () => void; // Redirects to account-app
	logout: () => Promise<void>;
}

/** Context の初期値 */
export const AuthContext = createContext<AuthState>({
	user: null,
	isAuthenticated: false,
	isAuthLoading: true,
	hasFetched: false,
	checkAuth: async () => {},
	login: () => {},
	logout: async () => {},
});

interface AuthProviderProps {
	children: React.ReactNode;
}

const PROTECTED_PATH_PREFIXES = [
	"/checkout",
	"/payment-success",
	"/mypage",
	"/organizer",
];

/**
 * AuthProvider:
 *  - Cookie を利用した認証状態をグローバルに管理
 *  - BroadcastChannel で他タブとの認証状態共有
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const [user, setUser] = useState<User | null>(null);
	const [isAuthLoading, setIsAuthLoading] = useState(true);
	const [hasFetched, setHasFetched] = useState(false);
	const authChannelRef = useRef<BroadcastChannel | null>(null);

	const isAuthenticated = !!user;

	const checkAuth = useCallback(async () => {
		setIsAuthLoading(true);
		const token = getAuthToken();

		if (!token) {
			setUser(null);
			setIsAuthLoading(false);
			setHasFetched(true);
			return;
		}

		try {
			const response = await apiClient.get("/user");
			if (response.status === 200) {
				const userData = {
					...response.data.user,
					name: response.data.name,
					address: response.data.address,
				};
				setUser(userData);
			} else {
				setUser(null);
			}
		} catch (_error) {
			clearAuthToken();
			setUser(null);
		} finally {
			setIsAuthLoading(false);
			setHasFetched(true);
		}
	}, []);

	useEffect(() => {
		checkAuth();

		const channel = new BroadcastChannel("auth");
		authChannelRef.current = channel;

		// 他タブからのログイン・ログアウト情報を受信
		channel.onmessage = (event) => {
			const { type, data } = event.data;

			if (type === "LOGIN") {
				// data.user には name と address が含まれている想定
				setUser(data.user);
				setIsAuthLoading(false);
				setHasFetched(true);
			} else if (type === "LOGOUT") {
				setUser(null);
				setIsAuthLoading(false);
				setHasFetched(true);
			}
		};

		return () => {
			channel.close();
			if (authChannelRef.current === channel) {
				authChannelRef.current = null;
			}
		};
	}, [checkAuth]);

	// ログインページへリダイレクト
	const pathRequiresAuth = useCallback(
		(pathname: string) => {
			return PROTECTED_PATH_PREFIXES.some((prefix) => {
				if (
					prefix === "/organizer" &&
					(pathname === "/organizer/register" ||
						pathname.startsWith("/organizer/register"))
				) {
					return false;
				}

				return pathname === prefix || pathname.startsWith(`${prefix}/`);
			});
		},
		[]
	);

	const login = useCallback(() => {
		const currentUrl = window.location.href;
		const loginUrl = getAccountLoginUrl();

		// リダイレクトURLを含めて遷移
		window.location.href = `${loginUrl}?redirect=${encodeURIComponent(
			currentUrl
		)}`;
	}, []);

	const logout = useCallback(async () => {
		const currentUrl = window.location.href;
		const loginUrl = getAccountLoginUrl();
		const requiresAuthRedirect = pathRequiresAuth(window.location.pathname);
		const params = new URLSearchParams();
		params.set("logout", "1");
		params.set("redirect", currentUrl);
		if (requiresAuthRedirect) {
			params.set("redirectRequiresAuth", "1");
			params.set("publicFallback", getTicketAppHomeUrl());
		}
		try {
			await apiClient.post("/logout");
			setUser(null);
		} catch (_error) {
			setUser(null);
		} finally {
			clearAuthToken();
			const channel = authChannelRef.current;
			if (channel) {
				try {
					channel.postMessage({ type: "LOGOUT" });
				} catch {}
			}

			const redirectUrl = `${loginUrl}?${params.toString()}`;
			window.location.href = redirectUrl;
		}
	}, [pathRequiresAuth]);

	const authState = {
		user,
		isAuthenticated,
		isAuthLoading,
		hasFetched,
		checkAuth,
		login,
		logout,
	};

	return (
		<AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
	);
};

/** AuthContext を使うヘルパーフック */
export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
